/**
 * O botão de começar (ou voltar para) uma sessão de assistir junto.
 *
 * Porte de `lib/features/watch_party/presentation/widgets/start_watch_party_button.dart`.
 * São três formas do mesmo botão — a pílula da ficha do filme, o cartão do
 * painel e o ícone que fica no canto do cartaz — e uma lógica só por baixo:
 *
 *  - sem sessão aberta, abre uma;
 *  - com sessão aberta na mesma coisa, só volta para ela;
 *  - com sessão aberta em outra coisa, troca o filme da sala e avisa disso na
 *    cor e no desenho antes de a pessoa tocar.
 *
 * Fora do perfil do casal o botão não existe — como no Flutter, que devolve um
 * `SizedBox.shrink()`.
 */

import { useNavigate } from 'react-router-dom';
import {
  COLECAO_SALAS,
  type FichaDaSala,
  type FonteDaSala as Fonte,
  abrirSala,
  daParaTocar,
  fonteDiferente,
  idDaSala,
  lerSala,
  limparConversas,
  traduzir,
  trocarMidia,
  usarDocumento,
  usarDupla,
} from '@nosso-sempre/shared';
import { Icone, type NomeDoIcone } from '../../componentes/Icone';
import { avisar } from './pecas';

export type FormaDoBotao = 'pilula' | 'cartao' | 'icone';

interface Props {
  midia: Fonte;
  forma?: FormaDoBotao;
  /** A ficha do filme troca o texto por "Assistir com a Ana", por exemplo. */
  rotulo?: string;
}

/** O caminho da sessão. É uma tela só, sempre a mesma sala. */
export const CAMINHO_DA_SALA = '/cinema/sala';

export function BotaoAssistirJunto({ midia, forma = 'pilula', rotulo }: Props) {
  const ir = useNavigate();
  const dupla = usarDupla();
  const id = dupla.parceiro ? idDaSala(dupla.eu, dupla.parceiro) : '';
  const ficha = usarDocumento<FichaDaSala>(COLECAO_SALAS, id);
  const sala = id ? lerSala(id, ficha) : null;

  const temSessao = sala !== null && sala.ativa;
  const difere = temSessao && sala !== null && fonteDiferente(sala, midia);

  /* Numa casa de três não há dupla, e sem dupla não há sessão. */
  if (!dupla.parceiro) return null;

  const tocar = async () => {
    let limpar = false;
    try {
      if (temSessao && sala) {
        if (daParaTocar(midia) && fonteDiferente(sala, midia)) {
          await trocarMidia(sala.id, dupla.eu, midia);
          limpar = true;
        }
      } else {
        if (!daParaTocar(midia)) {
          avisar(traduzir('Escolha o que assistir antes de começar a sessão.'));
          return;
        }
        await abrirSala(dupla, midia);
        limpar = true;
      }
    } catch {
      /* A sessão inteira vive no armazém. Quando a gravação não passa, abrir o
         player mesmo assim mostrava a sala antiga — sem canal e sem id de
         catálogo, tela preta. Melhor dizer que não deu. */
      avisar(traduzir('Não deu para abrir a sessão agora. Tente de novo em alguns minutos.'));
      return;
    }

    if (limpar) await limparConversas(id);
    ir(CAMINHO_DA_SALA);
  };

  const estado = difere ? 'troca' : temSessao ? 'volta' : 'nova';
  const desenho: NomeDoIcone = difere ? 'recarregar' : temSessao ? 'retroceder' : 'coracao';
  const texto =
    rotulo ??
    (difere
      ? traduzir('Trocar para este')
      : temSessao
        ? traduzir('Voltar à sessão')
        : traduzir('Assistir Junto'));

  if (forma === 'icone') {
    return (
      <button
        type="button"
        className={`cine-junto-icone ${estado}`}
        onClick={(e) => {
          /* O ícone mora dentro do cartaz, que também é botão: sem isto o
             toque abriria a ficha do filme em vez de começar a sessão. */
          e.stopPropagation();
          void tocar();
        }}
        aria-label={texto}
        title={texto}
      >
        <Icone nome={desenho} tamanho={16} preenchido={!difere && !temSessao} />
      </button>
    );
  }

  if (forma === 'cartao') {
    return (
      <button type="button" className={`cine-junto-cartao ${estado}`} onClick={() => void tocar()}>
        <span className="cine-junto-cartao-selo">
          <Icone nome={desenho} tamanho={18} preenchido={!difere && !temSessao} />
        </span>
        <span className="cine-junto-cartao-texto">
          <strong>{texto}</strong>
          <small>
            {difere
              ? traduzir('A sessão está tocando outra coisa.')
              : temSessao
                ? sala?.titulo || traduzir('Sessão aberta agora.')
                : traduzir('Vejam ao mesmo tempo, cada um no seu sofá.')}
          </small>
        </span>
        <Icone nome="avancar" tamanho={14} />
      </button>
    );
  }

  return (
    <button type="button" className={`cine-junto-pilula ${estado}`} onClick={() => void tocar()}>
      <Icone nome={desenho} tamanho={17} preenchido={!difere && !temSessao} />
      {texto}
    </button>
  );
}
